/**
 * O protocolo do caso resumido em quatro colunas (E78.F3.T05).
 *
 * O protocolo é o insumo de TODAS as peças (ADR-0077), e o formulário de oito
 * seções sempre aberto era o que fazia a tela parecer "tudo jogado": no caso
 * real ele reaparecia cinco vezes, uma por CCB. Aqui ele vira quatro colunas de
 * fatos — signatário, assinatura, trilha de aceite, rede e localização — mais a
 * lista do que a plataforma não informou.
 *
 * Função pura: nada de estado nem de I/O, e nada de conclusão. Ausência é dita
 * como ausência ("não informado no protocolo"), nunca como divergência — quem
 * confronta é o motor, e o resultado dele mora no quadro.
 */
import { ptBR } from '@/i18n/pt-BR';
import type {
  EventoProtocoloData,
  IpProtocoloData,
  ProtocoloAssinaturaCasoData,
} from '@/lib/exame_eletronico_protocolo';

const t = ptBR.exameEletronico.resumoProtocolo;
const tf = ptBR.exameEletronico.form;
const tm = ptBR.mesaEletronica;

export type ChaveColuna = 'signatario' | 'assinatura' | 'trilha' | 'rede';

export interface ColunaDoResumo {
  chave: ChaveColuna;
  rotulo: string;
  linhas: string[];
}

export interface ResumoDoProtocolo {
  /** Plataforma e código de conferência — a identidade do protocolo. */
  cabecalho: string;
  colunas: ColunaDoResumo[];
  /** O que a plataforma não informou, em texto corrido de leitura curta. */
  ausencias: string[];
}

const FORMATO_INSTANTE = new Intl.DateTimeFormat('pt-BR', {
  dateStyle: 'short',
  timeStyle: 'short',
  timeZone: 'UTC',
});

function instante(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const data = new Date(iso);
  return Number.isNaN(data.getTime()) ? iso : `${FORMATO_INSTANTE.format(data)} UTC`;
}

/** O instante comparável do evento: UTC quando houver, senão o local. */
function quando(evento: EventoProtocoloData): string | null {
  return instante(evento.em_utc) ?? instante(evento.em_local);
}

function preenchido(valor: string | null | undefined): valor is string {
  return typeof valor === 'string' && valor.trim() !== '';
}

function cabecalhoDo(protocolo: ProtocoloAssinaturaCasoData): string {
  const plataforma = preenchido(protocolo.plataforma) ? protocolo.plataforma : t.plataformaAusente;
  return preenchido(protocolo.codigo_verificacao)
    ? `${plataforma} · ${protocolo.codigo_verificacao}`
    : plataforma;
}

function colunaSignatario(protocolo: ProtocoloAssinaturaCasoData): string[] {
  const { nome, cpf } = protocolo.signatario;
  const linhas: string[] = [];
  if (preenchido(nome)) linhas.push(nome);
  if (preenchido(cpf)) linhas.push(t.cpf.replace('{valor}', cpf));
  const contatos = [protocolo.signatario.email, protocolo.signatario.telefone].filter(preenchido);
  linhas.push(...contatos);
  return linhas;
}

function linhaDaBiometria(protocolo: ProtocoloAssinaturaCasoData): string {
  const bio = protocolo.biometria;
  if (!bio.presente) return t.semBiometria;
  return preenchido(bio.sessao_id)
    ? t.biometriaComSessao.replace('{valor}', bio.sessao_id)
    : t.biometriaSemSessao;
}

function colunaAssinatura(protocolo: ProtocoloAssinaturaCasoData): string[] {
  const linhas: string[] = [];
  if (protocolo.tipo_assinatura !== 'nao_informado') {
    linhas.push(tf.tipoAssinaturaOpcoes[protocolo.tipo_assinatura]);
  }
  linhas.push(linhaDaBiometria(protocolo));
  if (preenchido(protocolo.envelope_id)) {
    linhas.push(t.envelope.replace('{valor}', protocolo.envelope_id));
  }
  if (preenchido(protocolo.assinatura_id)) {
    linhas.push(t.assinaturaId.replace('{valor}', protocolo.assinatura_id));
  }
  return linhas;
}

function colunaTrilha(protocolo: ProtocoloAssinaturaCasoData): string[] {
  const eventos = protocolo.eventos;
  if (eventos.length === 0) return [];
  const linhas = [
    eventos.length === 1 ? t.eventoUnico : t.eventos.replace('{n}', String(eventos.length)),
  ];
  const primeiro = quando(eventos[0] as EventoProtocoloData);
  const ultimo = quando(eventos[eventos.length - 1] as EventoProtocoloData);
  if (primeiro !== null) linhas.push(t.primeiroEvento.replace('{valor}', primeiro));
  if (ultimo !== null && eventos.length > 1) linhas.push(t.ultimoEvento.replace('{valor}', ultimo));
  return linhas;
}

function linhaDosIps(ips: readonly IpProtocoloData[]): string | null {
  if (ips.length === 0) return null;
  const cliente = ips.filter((ip) => ip.papel === 'cliente').length;
  const molde = ips.length === 1 ? t.enderecoUnico : t.enderecos;
  return molde
    .replace('{n}', String(ips.length))
    .replace('{cliente}', String(cliente))
    .replace('{servidor}', String(ips.length - cliente));
}

function colunaRede(protocolo: ProtocoloAssinaturaCasoData): string[] {
  const linhas: string[] = [];
  const ips = linhaDosIps(protocolo.ips);
  if (ips !== null) linhas.push(ips);
  const coordenada = protocolo.coordenada;
  if (coordenada !== null) {
    linhas.push(
      t.coordenada
        .replace('{lat}', coordenada.latitude.toFixed(6))
        .replace('{lon}', coordenada.longitude.toFixed(6)),
    );
    if (coordenada.fonte !== 'nao_informada' && typeof coordenada.precisao_m === 'number') {
      linhas.push(
        t.precisao
          .replace('{fonte}', tf.fonteCoordenadaOpcoes[coordenada.fonte])
          .replace('{valor}', String(Math.round(coordenada.precisao_m))),
      );
    }
  }
  return linhas;
}

/** Cada campo que a plataforma não informou, na ordem das colunas. */
function ausenciasDo(protocolo: ProtocoloAssinaturaCasoData): string[] {
  const a = t.ausencias;
  const { signatario: s, coordenada } = protocolo;
  const faltas: [boolean, string][] = [
    [!preenchido(protocolo.plataforma), a.plataforma],
    [!preenchido(protocolo.codigo_verificacao), a.codigoVerificacao],
    [!preenchido(s.nome), a.nome],
    [!preenchido(s.cpf), a.cpf],
    [!preenchido(s.email) && !preenchido(s.telefone), a.contato],
    [protocolo.tipo_assinatura === 'nao_informado', a.tipoAssinatura],
    [!preenchido(protocolo.envelope_id), a.envelope],
    [!preenchido(protocolo.assinatura_id), a.assinaturaId],
    [protocolo.eventos.length === 0, a.eventos],
    [protocolo.ips.length === 0, a.ips],
    [coordenada === null, a.coordenada],
    [
      coordenada !== null &&
        (coordenada.fonte === 'nao_informada' || typeof coordenada.precisao_m !== 'number'),
      a.precisao,
    ],
    [
      protocolo.dispositivo === null ||
        (!preenchido(protocolo.dispositivo.user_agent) &&
          !preenchido(protocolo.dispositivo.fingerprint)),
      a.dispositivo,
    ],
  ];
  return faltas.filter(([falta]) => falta).map(([, rotulo]) => rotulo);
}

export function resumoDoProtocolo(
  protocolo: ProtocoloAssinaturaCasoData | null,
): ResumoDoProtocolo | null {
  if (protocolo === null) return null;
  return {
    cabecalho: cabecalhoDo(protocolo),
    colunas: [
      { chave: 'signatario', rotulo: t.colunaSignatario, linhas: colunaSignatario(protocolo) },
      { chave: 'assinatura', rotulo: t.colunaAssinatura, linhas: colunaAssinatura(protocolo) },
      { chave: 'trilha', rotulo: t.colunaTrilha, linhas: colunaTrilha(protocolo) },
      { chave: 'rede', rotulo: t.colunaRede, linhas: colunaRede(protocolo) },
    ],
    ausencias: ausenciasDo(protocolo),
  };
}

/**
 * A frase de abertura do painel do caso (E78.F4): o que está sendo confrontado.
 *
 * Um caso eletrônico é N peças recebidas contra UM protocolo — e era isso que
 * a mesa não dizia: o perito via cinco quadros empilhados sem saber contra o
 * quê cada um foi conferido. Devolve segmentos, não uma frase pronta, para que
 * a tela decida como separá-los.
 */
export function fraseDoConfronto(
  protocolo: ProtocoloAssinaturaCasoData | null,
  totalDePecas: number,
): string[] {
  if (protocolo === null) return [tm.painelSemProtocolo];
  const molde = totalDePecas === 1 ? tm.painelUmaPeca : tm.painelPecas;
  const segmentos = [
    molde.replace('{n}', String(totalDePecas)).replace('{protocolo}', cabecalhoDo(protocolo)),
  ];
  const { nome, cpf } = protocolo.signatario;
  if (preenchido(nome)) segmentos.push(tm.painelSignatario.replace('{nome}', nome));
  if (preenchido(cpf)) segmentos.push(tm.painelCpf.replace('{cpf}', cpf));
  const assinatura = protocolo.eventos.filter((evento) => evento.tipo === 'assinatura').at(-1);
  const quandoAssinou = assinatura === undefined ? null : quando(assinatura);
  if (quandoAssinou !== null) {
    segmentos.push(tm.painelAssinaturaEm.replace('{quando}', quandoAssinou));
  }
  return segmentos;
}
